/**
 * user-layout-prefs.ts
 * Per-user layout preferences — which nav items are visible + their order,
 * and which home page sections are visible + their order.
 *
 * Stored in Firestore: users/{uid}/preferences/layout
 */

import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import type { FooterStyle } from '@/lib/footer-style';
import {
  ALL_NAV_ITEMS, HIDDEN_BY_DEFAULT_NAV, CORE_NAV_HREFS, MENU_VERSION,
  legacyDefaultNav, newMemberNav, mergeSavedNav, completeAdminDefault,
} from '@/lib/menu-config';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface UserNavPref {
  href: string;
  visible: boolean;
  order: number;
}

export interface UserHomeSectionPref {
  key: string;
  visible: boolean;
  order: number;
}

/** What the hero shows for this user (Gordon 2026-10-29: "once you logged in
 *  as a user, you can choose what you want to see in the hero or hide hero").
 *  Only narrows what the admin's hero settings already include. */
export interface UserHeroContentPrefs {
  videos: boolean;
  /** What's New messages, page tips and news/event/market image slides */
  updates: boolean;
  newReleases: boolean;
  /** Merch and apps for sale — off unless asked for. */
  market?: boolean;
  /** Music-business news — off unless asked for. */
  businessNews?: boolean;
}

export const DEFAULT_HERO_CONTENT: UserHeroContentPrefs = { videos: true, updates: true, newReleases: true };

/** The header message board (Gordon 2026-10-30): how much of the header it
 *  covers and what rolls by on it. */
export type HeaderBoardSize = 'full' | 'half' | 'off';
/** Kinds of message on the board — each can have its own font + colour. */
export type BoardItemKind = 'bb' | 'chat' | 'news' | 'release' | 'event' | 'product';
export const BOARD_ITEM_KINDS: BoardItemKind[] = ['chat', 'release', 'news', 'bb', 'event', 'product'];
export type BoardFont = 'impact' | 'condensed' | 'sans' | 'serif' | 'mono' | 'script';
export const BOARD_FONTS: Record<BoardFont, string> = {
  impact: "'Oswald', 'Arial Narrow', Impact, sans-serif",
  condensed: "'Roboto Condensed', 'Arial Narrow', 'Helvetica Neue', sans-serif-condensed, sans-serif",
  sans: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
  script: "'Brush Script MT', 'Segoe Script', 'Snell Roundhand', cursive",
};
/** `color: ''` = the active theme's text colour. */
export interface BoardStyle { font: BoardFont; color: string }
// One condensed sans in the theme's own colours for every kind of message
// (Gordon 2026-10-31: "text true to active theme colors and unified condensed
// sans serif font for all messages as default"). People can still pick their
// own per kind / per person in Settings → Layout.
const THEME_CONDENSED: BoardStyle = { font: 'condensed', color: '' };
export const DEFAULT_BOARD_STYLES: Record<BoardItemKind, BoardStyle> = {
  chat: THEME_CONDENSED,
  release: THEME_CONDENSED,
  news: THEME_CONDENSED,
  bb: THEME_CONDENSED,
  event: THEME_CONDENSED,
  product: THEME_CONDENSED,
};
export interface UserHeaderBoardPrefs {
  size: HeaderBoardSize;
  sources: {
    /** Messages BB posts from Super Admin */
    bb: boolean;
    /** Unread chat messages — click to answer */
    chat: boolean;
    releases: boolean;
    /** Music tech news */
    news: boolean;
    events: boolean;
    products: boolean;
  };
  /** Which news: music tech, music business, BB's own (never general news
   *  about artists outside BB — Gordon 2026-10-30) */
  newsTopics?: NewsTopics;
  /** Font + colour per kind of message (missing → the board default) */
  styles?: Partial<Record<BoardItemKind, BoardStyle>>;
  /** A special font + colour for a particular person, wherever they appear */
  people?: Record<string, BoardStyle & { name: string; image?: string }>;
}
export interface NewsTopics { tech: boolean; business: boolean; bb: boolean }
export const DEFAULT_NEWS_TOPICS: NewsTopics = { tech: true, business: true, bb: true };
export const DEFAULT_HEADER_BOARD_SOURCES: UserHeaderBoardPrefs['sources'] = { bb: true, chat: true, releases: true, news: true, events: true, products: true };
/** Music makers get the whole header, everyone else half (Gordon: "music
 *  makers 100% and consumer users maybe 50%"). */
export function defaultHeaderBoardPrefs(accountType?: string): UserHeaderBoardPrefs {
  const t = (accountType || '').toLowerCase();
  const maker = t === 'creator' || t === 'admin' || t === 'superadmin';
  return { size: maker ? 'full' : 'half', sources: { ...DEFAULT_HEADER_BOARD_SOURCES } };
}

/** Which side the menu, main VU meter and menu button sit on (left- or
 *  right-handed users); the chat and player buttons and search swap with them. */
export type MenuSide = 'left' | 'right';

/** Desktop player bar height: compact = the header's 50px (default), medium 64px, tall 80px. */
export type PlayerSize = 'compact' | 'medium' | 'tall';

export interface UserLayoutPrefs {
  nav: UserNavPref[];
  homeSections: UserHomeSectionPref[];
  heroContent?: UserHeroContentPrefs;
  menuSide?: MenuSide;
  headerBoard?: UserHeaderBoardPrefs;
  /** Their own footer player background (lib/footer-style.ts). */
  footerStyle?: FooterStyle;
  /** Menu defaults applied to this saved menu (1 = Dashboard-tab pages hidden). */
  navDefaultsVersion?: number;
  /** Set by getUserLayoutPrefs when it just applied a newer menu default. */
  navMigrated?: boolean;
  /** 2 = a menu made from the per-account-type defaults (new members since
   *  Phase A, or after "Reset to default"); missing = an older menu. */
  menuVersion?: number;
  /** Set by getUserLayoutPrefs: false when the doc has no `nav` of its own. */
  navSaved?: boolean;
  /** coπ's voice: '' = the device's own, else a Gemini voice id (lib/copi-voices). */
  copiVoice?: string;
  /** Show the list of things to say while holding coπ (Gordon 2026-11-05) — default on. */
  copiCommandList?: boolean;
  /** Desktop player bar height (Gordon 2026-11-01): header height by default. */
  playerSize?: PlayerSize;
  /** Their own button outline thickness, e.g. "1px" (lib/button-outline.ts);
   *  '' = BB's default. */
  buttonOutline?: string;
  /** Their own text/icon shadow amount, '0'…'3' (lib/text-shadow.ts); '' = BB's default. */
  textShadow?: string;
  updatedAt?: any;
}

/* ------------------------------------------------------------------ */
/*  Master item/section lists (mirrors sidebar-nav + home page)       */
/* ------------------------------------------------------------------ */

/** Menu items every signed-in user always has (Home, Dashboard, Profile,
 *  Settings) — from lib/menu-config, where the whole menu is defined. */
export { CORE_NAV_HREFS };

// The list itself lives in lib/menu-config (pure, so server code can read it too).
export { ALL_NAV_ITEMS };

// Order mirrors the canonical home layout (Releases, Videos, Artists, Charts,
// Events, Magazines, News, Demos, …) — this list seeds DEFAULT section prefs,
// so keys missing here used to sort to the END for guests/new users, which is
// how Magazines + Demos drifted out of position (Gordon: "put news after
// magazine").
export const ALL_HOME_SECTIONS: { key: string; title: string }[] = [
  // The hero (videos, news, tips) — signed-in users can hide it (Gordon 2026-10-29:
  // "make so you can hide hero as user in customize home page settings"). Visitors always get it.
  { key: 'hero',              title: 'Hero (videos, news & tips)' },
  { key: 'new-releases',      title: 'Releases' },
  { key: 'videos',            title: 'Videos' },
  { key: 'featured-artists',  title: 'Artists' },
  { key: 'top-charts',        title: 'Charts' },
  { key: 'live-events',       title: 'Events' },
  { key: 'magazines',         title: 'Magazines' },
  { key: 'news',              title: 'News' },
  { key: 'demos',             title: 'Demos & Beats' },
  { key: 'featured-labels',   title: 'Labels' },
  { key: 'popular-playlists', title: 'Playlists' },
  { key: 'marketplace',       title: 'Marketplace Highlights' },
  { key: 'radio',             title: 'Radio' },
  { key: 'community',         title: 'Community Picks' },
  { key: 'users',             title: 'People' },
];

/* ------------------------------------------------------------------ */
/*  Default presets by role                                            */
/* ------------------------------------------------------------------ */

/** The menu a member who never saved one has (unchanged by Phase A):
 *  admins everything but the More pages, everyone else Home, Dashboard,
 *  Profile, Settings, Beta demo and Collab. */
export function getDefaultNavPrefs(accountType?: string): UserNavPref[] {
  return legacyDefaultNav(accountType);
}

/** A new member's menu (per account type, lib/menu-config MENU_V2_DEFAULTS). */
export function getNewMemberNavPrefs(accountType?: string): UserNavPref[] {
  return newMemberNav(accountType);
}

/** The starting menu for a new member or a reset: Super Admin's saved
 *  default when there is one, else the per-account-type default. */
export async function resolveNewMemberNav(accountType?: string): Promise<UserNavPref[]> {
  const adminDefault = await getAdminNavDefaults();
  return adminDefault || newMemberNav(accountType);
}

export function getDefaultHomeSectionPrefs(): UserHomeSectionPref[] {
  return ALL_HOME_SECTIONS.map((s, i) => ({
    key: s.key,
    visible: true,
    order: i,
  }));
}

/* ------------------------------------------------------------------ */
/*  Firestore CRUD                                                     */
/* ------------------------------------------------------------------ */

const PREFS_PATH = (uid: string) =>
  doc(db, 'users', uid, 'preferences', 'layout');

export async function getUserLayoutPrefs(uid: string, accountType?: string): Promise<UserLayoutPrefs | null> {
  try {
    const snapshot = await getDoc(PREFS_PATH(uid));
    if (!snapshot.exists()) return null;

    const data = snapshot.data() as UserLayoutPrefs;

    // Guard against legacy/partial docs that predate one of these fields —
    // a missing array used to throw here, dropping the user back to defaults
    // (the "my menu didn't remember" bug).
    // Drop saved entries whose page is gone (merged hubs) and add the ones
    // this menu has never seen — older menus get only pre-Phase-A items
    // switched on, so nobody's menu changes; anything newer arrives OFF
    // (lib/menu-config mergeSavedNav).
    const navSaved = Array.isArray(data.nav);
    const mergedNav = mergeSavedNav(navSaved ? data.nav : [], { menuVersion: data.menuVersion, accountType });
    const savedSections = Array.isArray(data.homeSections) ? data.homeSections : [];

    // Merge any newly added home sections (default visible)
    const existingSectionKeys = new Set(savedSections.map(s => s.key));
    const mergedSections = [...savedSections];
    for (const section of ALL_HOME_SECTIONS) {
      if (!existingSectionKeys.has(section.key)) {
        mergedSections.push({ key: section.key, visible: true, order: mergedSections.length });
      }
    }

    // Once per account: the pages that became Dashboard tabs leave the menu
    // (version 1, 2026-11-01). Turning one back on later sticks.
    const navMigrated = (data.navDefaultsVersion ?? 0) < 1 && data.menuVersion !== MENU_VERSION;
    if (navMigrated) {
      for (const n of mergedNav) if (HIDDEN_BY_DEFAULT_NAV.has(n.href)) n.visible = false;
    }

    return {
      ...data,
      navMigrated,
      navSaved,
      nav: mergedNav,
      homeSections: mergedSections.sort((a, b) => a.order - b.order),
    };
  } catch (err) {
    console.error('Error fetching user layout prefs:', err);
    return null;
  }
}

// Partial + merge write: callers update nav OR homeSections independently so a
// stale closure of one field can never clobber the other (the old non-merge
// setDoc wrote both every time, using whatever was captured in the closure).
export async function saveUserLayoutPrefs(
  uid: string,
  prefs: Partial<Omit<UserLayoutPrefs, 'updatedAt'>>
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (Array.isArray(prefs.nav)) {
    payload.nav = prefs.nav.map((n, i) => ({ ...n, order: i }));
  }
  if (Array.isArray(prefs.homeSections)) {
    payload.homeSections = prefs.homeSections.map((s, i) => ({ ...s, order: i }));
  }
  if (prefs.heroContent) payload.heroContent = prefs.heroContent;
  if (prefs.menuSide === 'left' || prefs.menuSide === 'right') payload.menuSide = prefs.menuSide;
  if (prefs.headerBoard) payload.headerBoard = prefs.headerBoard;
  if (prefs.footerStyle) payload.footerStyle = prefs.footerStyle;
  if (typeof prefs.navDefaultsVersion === 'number') payload.navDefaultsVersion = prefs.navDefaultsVersion;
  if (prefs.menuVersion === MENU_VERSION) payload.menuVersion = MENU_VERSION;
  if (typeof prefs.copiVoice === 'string') payload.copiVoice = prefs.copiVoice;
  if (typeof prefs.copiCommandList === 'boolean') payload.copiCommandList = prefs.copiCommandList;
  if (prefs.playerSize === 'compact' || prefs.playerSize === 'medium' || prefs.playerSize === 'tall') payload.playerSize = prefs.playerSize;
  if (typeof prefs.buttonOutline === 'string') payload.buttonOutline = prefs.buttonOutline;
  if (typeof prefs.textShadow === 'string') payload.textShadow = prefs.textShadow;
  await setDoc(PREFS_PATH(uid), payload, { merge: true });
}

/* ------------------------------------------------------------------ */
/*  Super Admin-configured default order (applies to every NEW user;   */
/*  once a user customizes their own order, their choice always wins)  */
/* ------------------------------------------------------------------ */

const ADMIN_NAV_DEFAULTS_PATH = () => doc(db, 'siteSettings', 'navDefaults');

/** Super Admin's saved default nav order/visibility, or null if never set
 *  (in which case the hardcoded getDefaultNavPrefs() below is used). */
export async function getAdminNavDefaults(): Promise<UserNavPref[] | null> {
  try {
    const snap = await getDoc(ADMIN_NAV_DEFAULTS_PATH());
    if (!snap.exists()) return null;
    const raw = snap.data()?.nav;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    // Pages that are gone drop out; Explore is always on; anything added
    // after the admin saved arrives off (lib/menu-config).
    return completeAdminDefault(raw as UserNavPref[]);
  } catch (err) {
    console.error('Error fetching admin nav defaults:', err);
    return null;
  }
}

/** Super Admin: save the default nav order/visibility every new user starts with. */
export async function saveAdminNavDefaults(nav: UserNavPref[]): Promise<void> {
  await setDoc(
    ADMIN_NAV_DEFAULTS_PATH(),
    { nav: nav.map((n, i) => ({ ...n, order: i })), menuVersion: MENU_VERSION, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/** Onboarding: a NEW member's starting menu (Super Admin's default, else
 *  the one for their account type), saved with menuVersion 2. Never touches
 *  a menu that's already saved. Returns whether it wrote. */
export async function initNewMemberNav(uid: string, accountType?: string): Promise<boolean> {
  const existing = await getUserLayoutPrefs(uid, accountType);
  if (existing?.navSaved) return false;
  const nav = await resolveNewMemberNav(accountType);
  await saveUserLayoutPrefs(uid, { nav, menuVersion: MENU_VERSION, navDefaultsVersion: 1 });
  return true;
}
